import SingleDegreeDampingImg from '../../Images/SingleDegreeDampingImg.jpg'
import DecrementImg from '../../Images/DecrementImg.jpg'
import FV1Img from '../../Images/FV1Img.jpg'
import FV2Img from '../../Images/FV2Img.jpg'
import FV3Img from '../../Images/FV3Img.jpg'
import FV4Img from '../../Images/FV4Img.jpg'
import FV5Img from '../../Images/FV5Img.jpg'

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Shared SVG helpers ────────────────────────────────────────────
function mkSpring(x0, x1, y, n=8) {
  const cW = (x1-x0)/n;
  const pts = [[x0,y]];
  for (let i=0;i<n;i++) {
    pts.push([x0+i*cW+cW*0.25, y-16]);
    pts.push([x0+i*cW+cW*0.75, y+16]);
  }
  pts.push([x1,y]);
  return pts.map((p,i)=>`${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
}

function WallV({x,y,h}) {
  const ticks=[];
  for(let ty=y;ty<y+h;ty+=18) ticks.push(ty);
  return (
    <>
      <rect x={x} y={y} width={12} height={h} fill="#888"/>
      {ticks.map(ty=>(
        <line key={ty} x1={x} y1={ty} x2={x-13} y2={ty+14} stroke="#888" strokeWidth="1.5"/>
      ))}
    </>
  );
}

function GroundH({x1,x2,y}) {
  const ticks=[];
  for(let tx=x1;tx<=x2;tx+=22) ticks.push(tx);
  return (
    <>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke="#555" strokeWidth="2"/>
      {ticks.map(tx=>(
        <line key={tx} x1={tx} y1={y} x2={tx-12} y2={y+16} stroke="#555" strokeWidth="1.5"/>
      ))}
    </>
  );
}

// ── Diagram 1: Initial Conditions — Three Case Waveforms ─────────
function DiagramInitialConditions() {
  const W=900, H=420;
  const ωn=2*Math.PI, dt=0.008, tMax=2.5;

  // Case A: x0=1, v0=0
  // Case B: x0=0, v0=2π  (v0/ωn = 1, so amplitude = 1, starts at zero going up)
  // Case C: x0=0.7, v0=2π*0.7  (mixed)
  const cases = [
    { x0:1,   v0:0,          color:'#c0392b', label:'x₀=A, ẋ₀=0',      name:'Pure displacement IC' },
    { x0:0,   v0:ωn,         color:'#2E75B6', label:'x₀=0, ẋ₀=Aωₙ',   name:'Pure velocity IC' },
    { x0:0.7, v0:ωn*0.7,     color:'#27ae60', label:'x₀≠0, ẋ₀≠0',     name:'Mixed IC' },
  ];

  const buildPts = (x0,v0) => {
    const arr=[];
    for(let t=0;t<=tMax;t+=dt) {
      const x = x0*Math.cos(ωn*t) + (v0/ωn)*Math.sin(ωn*t);
      arr.push([t,x]);
    }
    return arr;
  };

  const allCases = cases.map(c=>({ ...c, pts: buildPts(c.x0,c.v0) }));

  // Amplitude for each case
  const amps = cases.map(c=>Math.sqrt(c.x0*c.x0+(c.v0/ωn)*(c.v0/ωn)));

  // SVG mapping  t:[0,2.5]→[55,570]  y:[-1.1,1.1]→[195,55] per panel
  const panH = 120;
  const panels = [0,1,2].map(i=>({ yOff: 20+i*(panH+12) }));
  const mx = t => 55+(t/tMax)*515;
  const myP = (y, yOff) => yOff+panH/2 - y*(panH*0.44);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Free Undamped Response — Effect of Initial Conditions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="icA" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
        </defs>

        {/* ── Three waveform strips ────────────────────────── */}
        {allCases.map(({color,label,name,pts,x0,v0},ci) => {
          const {yOff} = panels[ci];
          const A = amps[ci];
          const phi = Math.atan2(v0/ωn, x0); // phase: x=A cos(ωt-φ), so φ=atan2(v0/ωn, x0)... A cos(ωt-φ) = x0 cosωt + v0/ωn sinωt means φ = atan2(v0/ωn, x0) but check: A cos(ωt-φ)=A cosφ cosωt + A sinφ sinωt => x0=A cosφ, v0/ωn=A sinφ
          const pathD = pts.map(([t,y],i)=>`${i===0?'M':'L'}${mx(t).toFixed(1)},${myP(y,yOff).toFixed(1)}`).join(' ');

          return (
            <g key={ci}>
              {/* Panel background */}
              <rect x={50} y={yOff} width={520} height={panH} rx={3} fill="#fafafa" stroke="#e8d0d0"/>

              {/* Zero line */}
              <line x1={50} y1={myP(0,yOff)} x2={572} y2={myP(0,yOff)} stroke="#ddd" strokeWidth="1"/>

              {/* Amplitude envelope lines */}
              <line x1={50} y1={myP(A,yOff)} x2={572} y2={myP(A,yOff)} stroke={color} strokeWidth="1" strokeDasharray="5,4" opacity="0.5"/>
              <line x1={50} y1={myP(-A,yOff)} x2={572} y2={myP(-A,yOff)} stroke={color} strokeWidth="1" strokeDasharray="5,4" opacity="0.5"/>

              {/* Period markers */}
              {[1,2].map(n=>(
                <line key={n} x1={mx(n)} y1={yOff} x2={mx(n)} y2={yOff+panH} stroke="#e8d0d0" strokeWidth="1" strokeDasharray="3,3"/>
              ))}

              {/* Waveform */}
              <path d={pathD} fill="none" stroke={color} strokeWidth="2.5"/>

              {/* IC dot at t=0 */}
              <circle cx={mx(0)} cy={myP(x0,yOff)} r={5} fill={color} stroke="#fff" strokeWidth="1.5"/>

              {/* Amplitude bracket */}
              <line x1={574} y1={myP(0,yOff)} x2={574} y2={myP(A,yOff)} stroke={color} strokeWidth="1.5"/>
              <line x1={570} y1={myP(0,yOff)} x2={578} y2={myP(0,yOff)} stroke={color} strokeWidth="1.5"/>
              <line x1={570} y1={myP(A,yOff)} x2={578} y2={myP(A,yOff)} stroke={color} strokeWidth="1.5"/>
              <text x={582} y={myP(A/2,yOff)+4} fontSize="11" fill={color} fontWeight="700">A={A.toFixed(2)}</text>

              {/* Left axis label */}
              <text x={20} y={myP(0,yOff)+4} textAnchor="middle" fontSize="11" fill="#888" transform={`rotate(-90,20,${myP(0,yOff)})`}>x(t)</text>
            </g>
          );
        })}

        {/* Shared time axis */}
        <line x1={50} y1={390} x2={573} y2={390} stroke="#555" strokeWidth="1.5" markerEnd="url(#icA)"/>
        {[0,0.5,1,1.5,2,2.5].map(t=>(
          <g key={t}>
            <line x1={mx(t)} y1={387} x2={mx(t)} y2={393} stroke="#555" strokeWidth="1.5"/>
            <text x={mx(t)} y={404} textAnchor="middle" fontSize="10" fill="#888">{t}s</text>
          </g>
        ))}
        <text x={580} y={394} fontSize="12" fill="#555">t</text>

        {/* ── RIGHT: formula panel ──────────────────────── */}
        <rect x={590} y={10} width={300} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={740} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">General Solution</text>

        {/* General formula box */}
        <rect x={596} y={38} width={288} height={70} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={602} y={56} fontSize="11" fill="#7B0D0D" fontWeight="700">x(t) = x₀cos(ωₙt) + (ẋ₀/ωₙ)sin(ωₙt)</text>
        <text x={740} y={72} textAnchor="middle" fontSize="13" fill="#555">≡</text>
        <text x={740} y={90} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">A cos(ωₙt − φ)</text>

        {/* Case panels */}
        {[
          { y:120, color:'#c0392b', label:'Case A: x₀=A, ẋ₀=0',
            lines:['A = x₀','φ = 0°','Cosine: peaks at t=0','Max PE at start, zero KE'] },
          { y:222, color:'#2E75B6', label:'Case B: x₀=0, ẋ₀=v₀',
            lines:['A = v₀/ωₙ','φ = 90° (π/2)','Sine: zero at t=0','Max KE at start, zero PE'] },
          { y:324, color:'#27ae60', label:'Case C: x₀≠0, ẋ₀≠0',
            lines:['A = √(x₀²+(ẋ₀/ωₙ)²)','φ = arctan(ẋ₀/(ωₙx₀))','Both PE and KE at t=0','General case'] },
        ].map(({y,color,label,lines})=>(
          <g key={y}>
            <rect x={596} y={y} width={288} height={94} rx={3} fill="#fafafa" stroke={color} strokeWidth="1.5"/>
            <text x={602} y={y+17} fontSize="12" fill={color} fontWeight="700">{label}</text>
            {lines.map((l,i)=><text key={i} x={602} y={y+33+i*16} fontSize="11" fill="#444">{l}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The free undamped response x(t) = x₀cos(ωₙt) + (ẋ₀/ωₙ)sin(ωₙt) always reduces to A·cos(ωₙt − φ). The amplitude A and phase φ are completely fixed by the initial displacement x₀ and velocity ẋ₀. Critically, the natural frequency ωₙ = √(k/m) is independent of amplitude — a hallmark of linear systems.
      </div>
    </div>
  );
}

// ── Diagram 2: Phase Plane — Initial Conditions ───────────────────
function DiagramPhasePlane() {
  const W=900, H=360;
  const ωn=1;

  // Three trajectories in phase plane (x, ẋ/ωn) — all closed ellipses
  const cases = [
    { x0:1.0, v0:0,   color:'#c0392b', label:'x₀=1, ẋ₀=0' },
    { x0:0,   v0:1.5, color:'#2E75B6', label:'x₀=0, ẋ₀=1.5' },
    { x0:0.6, v0:0.8, color:'#27ae60', label:'x₀=0.6, ẋ₀=0.8' },
  ];

  const cx=210, cy=175, scX=100, scY=100;
  const px = x  => cx+x*scX;
  const py = v  => cy-v*scY;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Phase Plane — Each Initial Condition Produces a Unique Ellipse</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="ppA" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
        </defs>

        {/* ── LEFT: phase plane ──────────────────────── */}
        <rect x={10} y={10} width={400} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={210} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Phase Plane  (x vs ẋ/ωₙ)</text>

        {/* Grid */}
        {[-1.5,-1,-0.5,0,0.5,1,1.5].map(v=>(
          <g key={v}>
            <line x1={px(-1.6)} y1={py(v)} x2={px(1.6)} y2={py(v)} stroke="#f5eaea" strokeWidth={v===0?1.5:0.7}/>
            <line x1={px(v)} y1={py(-1.6)} x2={px(v)} y2={py(1.6)} stroke="#f5eaea" strokeWidth={v===0?1.5:0.7}/>
            {v!==0 && <text x={px(v)-2} y={cy+14} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>}
            {v!==0 && <text x={cx-14} y={py(v)+4} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>}
          </g>
        ))}

        {/* Ellipses for each case (undamped → perfect ellipse rx=A, ry=A*ωn/ωn=A) */}
        {cases.map(({x0,v0,color,label})=>{
          const A = Math.sqrt(x0*x0+(v0/ωn)*(v0/ωn));
          return (
            <g key={label}>
              <ellipse cx={cx} cy={cy} rx={A*scX} ry={A*scY} fill="none" stroke={color} strokeWidth="2.2" opacity="0.9"/>
              {/* IC dot */}
              <circle cx={px(x0)} cy={py(v0/ωn)} r={5} fill={color} stroke="#fff" strokeWidth="1.5"/>
              {/* Arrow showing direction of travel */}
              {(() => {
                // at IC, next step: x increases if v0>0, decreases if v0<0; ẋ changes by -ωn²x0
                const nx = x0 + 0.12*(v0);
                const nv = (v0/ωn) - 0.12*(ωn*x0);
                return (
                  <line x1={px(x0)} y1={py(v0/ωn)} x2={px(nx*0.96+x0*0.04)} y2={py(nv*0.96+(v0/ωn)*0.04)}
                    stroke={color} strokeWidth="2" markerEnd="url(#ppA)" opacity="0.7"/>
                );
              })()}
            </g>
          );
        })}

        {/* Axes */}
        <line x1={px(-1.55)} y1={cy} x2={px(1.6)} y2={cy} stroke="#555" strokeWidth="1.5" markerEnd="url(#ppA)"/>
        <line x1={cx} y1={py(1.6)} x2={cx} y2={py(-1.6)} stroke="#555" strokeWidth="1.5" markerEnd="url(#ppA)"/>
        <text x={px(1.65)} y={cy+5} fontSize="12" fill="#555">x</text>
        <text x={cx+6} y={py(1.65)} fontSize="12" fill="#555">ẋ/ωₙ</text>

        {/* Origin label */}
        <circle cx={cx} cy={cy} r={4} fill="#555"/>
        <text x={cx+7} y={cy-5} fontSize="11" fill="#555">O</text>

        {/* ── RIGHT: explanation panels ──────────── */}
        <rect x={420} y={10} width={470} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={655} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Phase Plane Properties</text>

        {/* Legend */}
        {cases.map(({color,label},i)=>(
          <g key={i}>
            <circle cx={432} cy={50+i*22} r={6} fill={color}/>
            <text x={444} y={55+i*22} fontSize="12" fill={color} fontWeight="700">{label}</text>
          </g>
        ))}

        {[
          { y:110, bg:'#f0fff4', bc:'#27ae60', label:'Ellipse equation',
            lines:['(x/A)² + (ẋ/(Aωₙ))² = 1','Semi-axis x: A (amplitude)','Semi-axis ẋ: Aωₙ (max velocity)','Every IC on same-energy ellipse'] },
          { y:214, bg:'#eaf3ff', bc:'#2E75B6', label:'Direction of travel',
            lines:['Clockwise (standard convention)','Upper half (ẋ>0): x increasing','Lower half (ẋ<0): x decreasing','Period = one full loop'] },
          { y:300, bg:'#FEF0F0', bc:'#c0392b', label:'Energy interpretation',
            lines:['Each ellipse = constant energy E','Larger ellipse = larger amplitude = more E','E = ½m(Aωₙ)² = ½kA²','Undamped: trajectory never changes'] },
        ].map(({y,bg,bc,label,lines})=>(
          <g key={y}>
            <rect x={426} y={y} width={458} height={82} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={432} y={y+17} fontSize="12" fill="#7B0D0D" fontWeight="700">{label}</text>
            {lines.map((l,i)=><text key={i} x={432} y={y+33+i*14} fontSize="11" fill="#444">{l}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        In the phase plane (x vs ẋ), each initial condition (x₀, ẋ₀) sits on a unique closed ellipse. The system traverses its ellipse clockwise at constant energy — upper half moves right (ẋ &gt; 0), lower half moves left. Larger initial energy produces a proportionally larger ellipse. This geometric view makes it immediately clear why amplitude depends on ICs but frequency does not.
      </div>
    </div>
  );
}

// ── Diagram 3: Rayleigh's Method — Spring–Mass with Spring Mass ──
function DiagramRayleigh() {
  const W=900, H=440;

  // For the energy plot: T_max = V_max as a function of assumed shape ωn
  // Rayleigh: ωn² = (k·A²) / (m·A² + ms/3·A²) = k/(m + ms/3)
  // Plot energy curves vs amplitude to show they balance

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Rayleigh's Method — Equating Maximum Kinetic &amp; Potential Energy</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="rA" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
          <marker id="rB" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#27ae60"/></marker>
          <marker id="rC" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#2E75B6"/></marker>
        </defs>

        {/* ═══ TOP ROW: three physical systems ═══ */}

        {/* ── System 1: Simple spring-mass ──────── */}
        <rect x={10} y={10} width={268} height={210} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={144} y={30} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">System 1: Simple Spring-Mass</text>

        <WallV x={18} y={50} h={130}/>
        <path d={mkSpring(30,160,115,8)} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
        <text x={95} y={97} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">k</text>
        <rect x={160} y={80} width={80} height={70} rx={5} fill="#2E75B6" stroke="#1a5276" strokeWidth="2"/>
        <text x={200} y={120} textAnchor="middle" fontSize="20" fill="#fff" fontWeight="700">m</text>
        <line x1={200} y1={78} x2={200} y2={58} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#rA)"/>
        <text x={208} y={56} fontSize="12" fill="#c0392b" fontWeight="700">x=A·sin(ωt)</text>

        <rect x={10} y={168} width={268} height={46} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={16} y={184} fontSize="11" fill="#7B0D0D" fontWeight="700">T_max = ½mA²ω²</text>
        <text x={16} y={199} fontSize="11" fill="#7B0D0D" fontWeight="700">V_max = ½kA²</text>
        <text x={16} y={214} fontSize="11" fill="#555">T_max=V_max  →  ωₙ=√(k/m)</text>

        {/* ── System 2: Spring-mass + spring mass ── */}
        <rect x={288} y={10} width={308} height={210} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={442} y={30} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">System 2: Spring with Distributed Mass</text>

        <WallV x={296} y={50} h={130}/>

        {/* Spring coils — color gradient to show distributed mass */}
        {Array.from({length:8},(_,i)=>{
          const x0r=308+i*36, x1r=308+(i+1)*36, yr=115;
          const frac=(i+0.5)/8;
          const g=Math.round(100+frac*100), col=`rgb(180,${g},60)`;
          return (
            <g key={i}>
              <line x1={x0r} y1={yr} x2={x0r+9} y2={yr-16} stroke={col} strokeWidth="2.5"/>
              <line x1={x0r+9} y1={yr-16} x2={x0r+27} y2={yr+16} stroke={col} strokeWidth="2.5"/>
              <line x1={x0r+27} y1={yr+16} x2={x1r} y2={yr} stroke={col} strokeWidth="2.5"/>
            </g>
          );
        })}
        <text x={442} y={97} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">k, mₛ (spring mass)</text>

        <rect x={598} y={80} width={80} height={70} rx={5} fill="#2E75B6" stroke="#1a5276" strokeWidth="2"/>
        <text x={638} y={120} textAnchor="middle" fontSize="20" fill="#fff" fontWeight="700">m</text>
        <line x1={638} y1={78} x2={638} y2={58} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#rA)"/>

        {/* Distributed velocity profile annotation */}
        {[0,1,2,3,4].map(i=>{
          const xp=308+i*72, vlen=i*10;
          return <line key={i} x1={xp} y1={115} x2={xp} y2={115-vlen} stroke="#c0392b" strokeWidth="1.5" opacity="0.5" markerEnd="url(#rA)"/>;
        })}
        <text x={340} y={62} fontSize="10" fill="#c0392b" fontStyle="italic">velocity∝ position</text>

        <rect x={288} y={168} width={308} height={46} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={294} y={184} fontSize="11" fill="#7B0D0D" fontWeight="700">T_max = ½(m + mₛ/3)A²ω²</text>
        <text x={294} y={199} fontSize="11" fill="#7B0D0D" fontWeight="700">V_max = ½kA²</text>
        <text x={294} y={214} fontSize="11" fill="#555">ωₙ = √(k / (m + mₛ/3))</text>

        {/* ── System 3: Cantilever beam ─────────── */}
        <rect x={606} y={10} width={284} height={210} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={748} y={30} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">System 3: Cantilever Beam + Tip Mass</text>

        {/* Wall for cantilever */}
        <rect x={614} y={70} width={12} height={90} fill="#888"/>
        {[70,88,106,124,142,158].map(y=>(<line key={y} x1={614} y1={y} x2={601} y2={y+16} stroke="#888" strokeWidth="1.5"/>))}

        {/* Beam */}
        <rect x={626} y={108} width={200} height={14} rx={2} fill="#8B6914" stroke="#5a4208" strokeWidth="1.5"/>
        <text x={726} y={104} textAnchor="middle" fontSize="11" fill="#8B6914" fontWeight="700">EI, L, m_b</text>

        {/* Deflected shape (dashed) */}
        <path d="M 626,115 Q 726,115 826,140" fill="none" stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3"/>
        <text x={820} y={135} fontSize="11" fill="#c0392b" fontStyle="italic">δ(x)</text>

        {/* Tip mass */}
        <rect x={828} y={138} width={46} height={36} rx={4} fill="#2E75B6" stroke="#1a5276" strokeWidth="2"/>
        <text x={851} y={160} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">m</text>

        {/* Vertical force arrow at tip */}
        <line x1={851} y1={136} x2={851} y2={108} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#rA)"/>

        <rect x={606} y={168} width={284} height={46} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1"/>
        <text x={612} y={184} fontSize="11" fill="#7B0D0D" fontWeight="700">m_eq = m + (33/140)m_b</text>
        <text x={612} y={199} fontSize="11" fill="#7B0D0D" fontWeight="700">k_eq = 3EI/L³</text>
        <text x={612} y={214} fontSize="11" fill="#555">ωₙ = √(3EI/L³ / m_eq)</text>

        {/* ═══ BOTTOM ROW: Rayleigh method flowchart ═══ */}
        <rect x={10} y={228} width={880} height={202} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={450} y={248} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Rayleigh's Method — Step-by-Step Procedure</text>

        {/* Step boxes */}
        {[
          { x:20,  w:158, bg:'#eaf3ff', bc:'#2E75B6', step:'1', title:'Assume shape',
            lines:['Guess a deflection shape','u(x) satisfying BCs','e.g. u(x)=(x/L)² for beam'] },
          { x:190, w:158, bg:'#f0fff4', bc:'#27ae60', step:'2', title:'Write T_max',
            lines:['T_max = ½ω²∫ρA[u(x)]²dx','For lumped: T=½ω²Σmᵢuᵢ²','Factor out ω² and A²'] },
          { x:360, w:158, bg:'#fff5e6', bc:'#e07b00', step:'3', title:'Write V_max',
            lines:['V_max = ½∫EI[u″(x)]²dx','For spring: V=½kA²','Factor out A²'] },
          { x:530, w:158, bg:'#FEF0F0', bc:'#c0392b', step:'4', title:'Equate & solve',
            lines:['Set T_max = V_max','Cancel A²; solve for ω²','ω² = V_max* / T_max*'] },
          { x:700, w:178, bg:'#f5d0ff', bc:'#9b59b6', step:'5', title:'Rayleigh quotient',
            lines:['ωₙ² = ∫EI(u″)²dx / ∫ρA(u)²dx','Always overestimates ωₙ','Better shape → closer result'] },
        ].map(({x,w,bg,bc,step,title,lines})=>(
          <g key={x}>
            <rect x={x+2} y={256} width={w} height={168} rx={4} fill={bg} stroke={bc} strokeWidth="1.5"/>
            <circle cx={x+18} cy={272} r={12} fill={bc}/>
            <text x={x+18} y={277} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">{step}</text>
            <text x={x+36} y={275} fontSize="12" fill="#7B0D0D" fontWeight="700">{title}</text>
            {lines.map((l,i)=><text key={i} x={x+8} y={296+i*18} fontSize="10" fill="#444">{l}</text>)}
          </g>
        ))}

        {/* Arrows between steps */}
        {[190,360,530,700].map(x=>(
          <polygon key={x} points={`${x-8},340 ${x+2},334 ${x+2},346`} fill="#999"/>
        ))}
      </svg>
      <div style={S.dCaption}>
        Rayleigh's method estimates the natural frequency by assuming the system vibrates in a given shape and equating the maximum kinetic energy to the maximum potential energy. The method always overestimates ωₙ (it is an upper bound) because any assumed shape other than the true mode shape introduces additional constraint stiffness. The closer the assumed shape to the true mode, the more accurate the result.
      </div>
    </div>
  );
}

// ── Diagram 4: Rayleigh — Energy Curves Visualisation ────────────
function DiagramRayleighEnergy() {
  const W=900, H=320;

  // Show T_max and V_max curves vs ω for fixed amplitude A=1
  // T_max = ½ m_eq A² ω²  (grows with ω²)
  // V_max = ½ k A²         (constant)
  // Intersection gives ωn
  const k=100, m=1, ms=0.3;
  const meq_exact = m;              // no spring mass
  const meq_approx= m + ms/3;       // spring mass correction
  const ωn_exact  = Math.sqrt(k/meq_exact);
  const ωn_approx = Math.sqrt(k/meq_approx);

  const ωMax=14, nPts=300;
  const mx = ω => 50+(ω/ωMax)*500;
  const myE= E => 60+(1-Math.min(E,100)/100)*220;

  const Vmax = 0.5*k;  // constant, A=1

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Rayleigh's Method — Energy Balance Diagram</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="reA" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
        </defs>

        {/* Graph */}
        <rect x={45} y={55} width={510} height={225} rx={3} fill="#fafafa" stroke="#ddd"/>

        {/* Grid */}
        {[0,25,50,75,100].map(v=>(
          <g key={v}>
            <line x1={45} y1={myE(v)} x2={557} y2={myE(v)} stroke="#ebebeb" strokeWidth="0.8"/>
            <text x={39} y={myE(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,2,4,6,8,10,12,14].map(ω=>(
          <g key={ω}>
            <line x1={mx(ω)} y1={55} x2={mx(ω)} y2={280} stroke="#ebebeb" strokeWidth="0.8"/>
            <text x={mx(ω)} y={294} textAnchor="middle" fontSize="10" fill="#888">{ω}</text>
          </g>
        ))}

        {/* V_max — horizontal line */}
        <line x1={45} y1={myE(Vmax)} x2={557} y2={myE(Vmax)} stroke="#27ae60" strokeWidth="2.5"/>
        <text x={562} y={myE(Vmax)+4} fontSize="12" fill="#27ae60" fontWeight="700">V_max = ½kA²</text>

        {/* T_max curves */}
        {/* Exact (no spring mass) */}
        {[
          { meq:meq_exact,  color:'#c0392b', dash:'',    label:'T_max (m only)' },
          { meq:meq_approx, color:'#e07b00', dash:'8,4', label:'T_max (m + mₛ/3)' },
        ].map(({meq,color,dash,label})=>{
          const pts=[];
          for(let i=0;i<=nPts;i++){
            const ω=(i/nPts)*ωMax;
            const T=0.5*meq*ω*ω;
            pts.push([ω,T]);
          }
          const d=pts.map(([ω,T],i)=>`${i===0?'M':'L'}${mx(ω).toFixed(1)},${myE(Math.min(T,100)).toFixed(1)}`).join(' ');
          return <path key={label} d={d} fill="none" stroke={color} strokeWidth="2.5" strokeDasharray={dash}/>;
        })}

        {/* Intersection markers */}
        {[
          { ωn:ωn_exact,  color:'#c0392b', label:`ωₙ=${ωn_exact.toFixed(2)} (exact)` },
          { ωn:ωn_approx, color:'#e07b00', label:`ωₙ=${ωn_approx.toFixed(2)} (corrected)` },
        ].map(({ωn,color,label})=>(
          <g key={ωn}>
            <line x1={mx(ωn)} y1={55} x2={mx(ωn)} y2={myE(Vmax)} stroke={color} strokeWidth="1.5" strokeDasharray="5,3"/>
            <circle cx={mx(ωn)} cy={myE(Vmax)} r={6} fill={color} stroke="#fff" strokeWidth="2"/>
            <text x={mx(ωn)} y={myE(Vmax)-12} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">{label}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={43} y1={55} x2={43} y2={283} stroke="#555" strokeWidth="1.5" markerEnd="url(#reA)"/>
        <line x1={41} y1={280} x2={560} y2={280} stroke="#555" strokeWidth="1.5" markerEnd="url(#reA)"/>
        <text x={18} y={170} textAnchor="middle" fontSize="12" fill="#555" transform="rotate(-90,18,170)">Energy (J),  A=1</text>
        <text x={400} y={311} textAnchor="middle" fontSize="12" fill="#555">ω  [rad/s]</text>

        {/* ── RIGHT: key insight panel ─── */}
        <rect x={574} y={10} width={316} height={H-15} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={732} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Key Insights</text>

        {[
          { y:36,  bg:'#f0fff4', bc:'#27ae60', label:'V_max is constant',
            lines:['V_max = ½kA² — no ω dependence','Horizontal green line','Set by stiffness and amplitude'] },
          { y:122, bg:'#FEF0F0', bc:'#c0392b', label:'T_max grows with ω²',
            lines:['T_max = ½m_eq A² ω²','Parabola through origin','Steeper for larger mass'] },
          { y:208, bg:'#fff5e6', bc:'#e07b00', label:'Spring mass correction',
            lines:['Spring mass mₛ adds mₛ/3 to m_eq','Lowers ωₙ (shifts intersection left)','mₛ/3 rule from linear velocity profile'] },
        ].map(({y,bg,bc,label,lines})=>(
          <g key={y}>
            <rect x={580} y={y} width={304} height={80} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={586} y={y+17} fontSize="12" fill="#7B0D0D" fontWeight="700">{label}</text>
            {lines.map((l,i)=><text key={i} x={586} y={y+32+i*16} fontSize="11" fill="#444">{l}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The natural frequency is found where T_max (kinetic, parabolic) intersects V_max (potential, constant horizontal line). Adding the spring's distributed mass (mₛ/3 correction) steepens the T_max parabola, shifting the intersection to a lower ωₙ. This diagram makes it visually clear why a heavier spring reduces the natural frequency.
      </div>
    </div>
  );
}

// ── Diagram 5: Equivalent Stiffness — Spring Combinations ────────
function DiagramEquivStiffness() {
  const W=900, H=420;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Equivalent Stiffness — Spring Combinations &amp; Structural Elements</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="esA" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
          <marker id="esB" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#2E75B6"/></marker>
        </defs>

        {/* ═══ TOP HALF: spring combinations (4 configs) ═══ */}

        {/* ── Config 1: Series ──── */}
        <rect x={10} y={10} width={200} height={190} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={110} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Series</text>
        <WallV x={18} y={40} h={90}/>
        <path d={mkSpring(30,100,85,5)} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
        <text x={65} y={70} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">k₁</text>
        <path d={mkSpring(100,170,85,5)} fill="none" stroke="#e07b00" strokeWidth="2.5"/>
        <text x={135} y={70} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">k₂</text>
        <rect x={170} y={65} width={32} height={40} rx={3} fill="#2E75B6" stroke="#1a5276" strokeWidth="1.5"/>
        <text x={186} y={88} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">m</text>
        <rect x={10} y={142} width={200} height={52} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={16} y={158} fontSize="11" fill="#7B0D0D" fontWeight="700">1/k_eq = 1/k₁ + 1/k₂</text>
        <text x={16} y={174} fontSize="11" fill="#444">k_eq = k₁k₂/(k₁+k₂)</text>
        <text x={16} y={190} fontSize="11" fill="#555" fontStyle="italic">softer than either spring</text>

        {/* ── Config 2: Parallel ── */}
        <rect x={220} y={10} width={200} height={190} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={320} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Parallel</text>
        <WallV x={228} y={40} h={110}/>
        <path d={mkSpring(240,340,70,6)} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
        <text x={290} y={54} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">k₁</text>
        <path d={mkSpring(240,340,115,6)} fill="none" stroke="#e07b00" strokeWidth="2.5"/>
        <text x={290} y={132} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">k₂</text>
        <line x1={240} y1={70} x2={240} y2={115} stroke="#bbb" strokeWidth="1.5" strokeDasharray="3"/>
        <line x1={340} y1={70} x2={340} y2={115} stroke="#bbb" strokeWidth="1.5" strokeDasharray="3"/>
        <rect x={340} y={78} width={32} height={40} rx={3} fill="#2E75B6" stroke="#1a5276" strokeWidth="1.5"/>
        <text x={356} y={101} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">m</text>
        <rect x={220} y={142} width={200} height={52} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={226} y={158} fontSize="11" fill="#7B0D0D" fontWeight="700">k_eq = k₁ + k₂</text>
        <text x={226} y={174} fontSize="11" fill="#444">ωₙ = √((k₁+k₂)/m)</text>
        <text x={226} y={190} fontSize="11" fill="#555" fontStyle="italic">stiffer than either spring</text>

        {/* ── Config 3: Cantilever ── */}
        <rect x={430} y={10} width={210} height={190} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={535} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Cantilever Beam</text>
        <rect x={438} y={55} width={12} height={90} fill="#888"/>
        {[55,72,89,106,123,140].map(y=>(<line key={y} x1={438} y1={y} x2={425} y2={y+15} stroke="#888" strokeWidth="1.5"/>))}
        <rect x={450} y={94} width={160} height={12} rx={2} fill="#8B6914" stroke="#5a4208" strokeWidth="1.5"/>
        <text x={530} y={90} textAnchor="middle" fontSize="11" fill="#8B6914" fontWeight="700">EI, L</text>
        <rect x={612} y={106} width={28} height={28} rx={3} fill="#2E75B6" stroke="#1a5276" strokeWidth="1.5"/>
        <text x={626} y={123} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">m</text>
        <line x1={626} y1={104} x2={626} y2={82} stroke="#c0392b" strokeWidth="2" markerEnd="url(#esA)"/>
        <text x={632} y={78} fontSize="10" fill="#c0392b" fontWeight="700">F</text>
        <rect x={430} y={142} width={210} height={52} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1"/>
        <text x={436} y={158} fontSize="11" fill="#7B0D0D" fontWeight="700">k_eq = 3EI/L³</text>
        <text x={436} y={174} fontSize="11" fill="#444">δ_tip = FL³/(3EI)</text>
        <text x={436} y={190} fontSize="11" fill="#555" fontStyle="italic">ωₙ=√(3EI/(L³·m_eq))</text>

        {/* ── Config 4: Simply supported beam ── */}
        <rect x={650} y={10} width={240} height={190} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={770} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Simply Supported Beam</text>
        {/* Left pin */}
        <polygon points="658,140 668,118 678,140" fill="#888"/>
        <line x1={658} y1={140} x2={678} y2={140} stroke="#888" strokeWidth="2"/>
        {/* Right pin */}
        <polygon points="862,140 872,118 882,140" fill="#888"/>
        <line x1={862} y1={140} x2={882} y2={140} stroke="#888" strokeWidth="2"/>
        {/* Beam */}
        <rect x={668} y={112} width={204} height={10} rx={2} fill="#8B6914" stroke="#5a4208" strokeWidth="1.5"/>
        <text x={770} y={108} textAnchor="middle" fontSize="11" fill="#8B6914" fontWeight="700">EI, L</text>
        {/* Mid-span mass */}
        <rect x={756} y={122} width={28} height={28} rx={3} fill="#2E75B6" stroke="#1a5276" strokeWidth="1.5"/>
        <text x={770} y={139} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">m</text>
        <line x1={770} y1={120} x2={770} y2={98} stroke="#c0392b" strokeWidth="2" markerEnd="url(#esA)"/>
        <text x={776} y={95} fontSize="10" fill="#c0392b" fontWeight="700">F</text>
        {/* Deflected shape */}
        <path d="M 668,117 Q 770,150 872,117" fill="none" stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3"/>
        <rect x={650} y={152} width={240} height={52} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={656} y={168} fontSize="11" fill="#7B0D0D" fontWeight="700">k_eq = 48EI/L³</text>
        <text x={656} y={184} fontSize="11" fill="#444">δ_mid = FL³/(48EI)</text>
        <text x={656} y={200} fontSize="11" fill="#555" fontStyle="italic">mid-span point load</text>

        {/* ═══ BOTTOM: equivalent single oscillator ═══ */}
        <rect x={10} y={210} width={880} height={200} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={450} y={230} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Reduction to Equivalent Single Oscillator</text>

        {/* Complex system box */}
        <rect x={20} y={244} width={220} height={120} rx={5} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={130} y={264} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">Complex System</text>
        <text x={130} y={282} textAnchor="middle" fontSize="11" fill="#555">multiple springs/beams</text>
        <text x={130} y={298} textAnchor="middle" fontSize="11" fill="#555">distributed masses</text>
        <text x={130} y={314} textAnchor="middle" fontSize="11" fill="#555">gears/levers</text>
        <text x={130} y={330} textAnchor="middle" fontSize="11" fill="#555">various BCs</text>
        <text x={130} y={354} textAnchor="middle" fontSize="11" fill="#555">k₁, k₂, m₁, m₂…</text>

        {/* Arrow */}
        <line x1={242} y1={304} x2={310} y2={304} stroke="#555" strokeWidth="2.5" markerEnd="url(#esA)"/>
        <text x={276} y={298} textAnchor="middle" fontSize="11" fill="#555" fontWeight="700">reduce</text>

        {/* Reduction steps */}
        <rect x={314} y={244} width={280} height={120} rx={5} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={454} y={264} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">Reduction Steps</text>
        {['1. Find k_eq: combine springs (series/parallel)',
          '2. Find m_eq: add correction terms',
          '   (mₛ/3 for springs, 33m_b/140 for beams)',
          '3. Apply gear/lever ratios if present',
          '4. Verify units: [N/m] and [kg]'].map((l,i)=>(
          <text key={i} x={320} y={281+i*18} fontSize="10" fill="#444">{l}</text>
        ))}

        {/* Arrow */}
        <line x1={596} y1={304} x2={664} y2={304} stroke="#555" strokeWidth="2.5" markerEnd="url(#esA)"/>
        <text x={630} y={298} textAnchor="middle" fontSize="11" fill="#555" fontWeight="700">result</text>

        {/* Equivalent oscillator */}
        <rect x={668} y={244} width={216} height={120} rx={5} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={776} y={264} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">SDOF Equivalent</text>
        <WallV x={678} y={268} h={70}/>
        <path d={mkSpring(690,790,302,7)} fill="none" stroke="#27ae60" strokeWidth="2"/>
        <text x={740} y={288} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">k_eq</text>
        <rect x={792} y={280} width={52} height={44} rx={4} fill="#2E75B6" stroke="#1a5276" strokeWidth="2"/>
        <text x={818} y={305} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">m_eq</text>
        <text x={776} y={356} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace">ωₙ = √(k_eq/m_eq)</text>
      </svg>
      <div style={S.dCaption}>
        Any linear mechanical system can be reduced to a single equivalent spring k_eq and equivalent mass m_eq. Springs in series comply (softest path wins); springs in parallel add. Structural beams contribute an equivalent point stiffness at the reference DOF. Distributed mass of springs and beams adds a correction fraction to m_eq. Once reduced, the natural frequency follows directly from ωₙ = √(k_eq/m_eq).
      </div>
    </div>
  );
}

// ── Diagram 6: Equivalent Mass — Gears, Levers & Beam Correction ─
function DiagramEquivMass() {
  const W=900, H=400;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Equivalent Mass — Gear Trains, Levers &amp; Distributed Mass Corrections</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="emA" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
          <marker id="emB" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#2E75B6"/></marker>
        </defs>

        {/* ── LEFT: Gear train ───────────────────── */}
        <rect x={10} y={10} width={280} height={380} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={150} y={30} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Gear Train — Reflected Inertia</text>

        {/* Gear 1 (driver) */}
        <circle cx={90} cy={140} r={55} fill="#f0f0f0" stroke="#888" strokeWidth="2"/>
        <circle cx={90} cy={140} r={10} fill="#888"/>
        {Array.from({length:12},(_,i)=>{
          const a=i*30*Math.PI/180;
          return <rect key={i} x={90+50*Math.cos(a)-4} y={140+50*Math.sin(a)-8} width={8} height={14} rx={2} fill="#888" transform={`rotate(${i*30},${90+50*Math.cos(a)},${140+50*Math.sin(a)})`}/>;
        })}
        <text x={90} y={118} textAnchor="middle" fontSize="12" fill="#555" fontWeight="700">J₁, N₁=20T</text>
        <text x={90} y={175} textAnchor="middle" fontSize="12" fill="#c0392b">τ_in, ω₁</text>

        {/* Gear 2 (output) */}
        <circle cx={195} cy={200} r={35} fill="#f0f0f0" stroke="#888" strokeWidth="2"/>
        <circle cx={195} cy={200} r={8} fill="#888"/>
        {Array.from({length:8},(_,i)=>{
          const a=i*45*Math.PI/180;
          return <rect key={i} x={195+30*Math.cos(a)-4} y={200+30*Math.sin(a)-6} width={7} height={12} rx={2} fill="#888" transform={`rotate(${i*45},${195+30*Math.cos(a)},${200+30*Math.sin(a)})`}/>;
        })}
        <text x={195} y={185} textAnchor="middle" fontSize="11" fill="#555" fontWeight="700">J₂, N₂=12T</text>
        <text x={195} y={248} textAnchor="middle" fontSize="11" fill="#2E75B6">ω₂=n·ω₁</text>

        {/* n label */}
        <text x={150} y={225} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">n=N₂/N₁</text>

        {/* Formula boxes */}
        <rect x={16} y={262} width={268} height={54} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1"/>
        <text x={22} y={280} fontSize="12" fill="#7B0D0D" fontWeight="700">Gear ratio:  n = ω₂/ω₁ = N₂/N₁</text>
        <text x={22} y={298} fontSize="11" fill="#444">n = 12/20 = 0.6 (speed reduction)</text>
        <text x={22} y={312} fontSize="11" fill="#444">τ₁/τ₂ = n  (torque amplified)</text>

        <rect x={16} y={322} width={268} height={62} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={22} y={340} fontSize="12" fill="#7B0D0D" fontWeight="700">Reflected inertia to shaft 1:</text>
        <text x={22} y={358} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">J_eq = J₁ + J₂·n²</text>
        <text x={22} y={374} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">      + m_load·r₂²·n²</text>

        {/* ── MIDDLE: Lever ──────────────────────── */}
        <rect x={300} y={10} width={270} height={380} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={435} y={30} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Lever — Reflected Mass</text>

        {/* Lever beam */}
        <rect x={310} y={155} width={250} height={12} rx={2} fill="#8B6914" stroke="#5a4208" strokeWidth="2"/>
        {/* Pivot */}
        <polygon points="415,167 425,192 435,167" fill="#888"/>
        <line x1={415} y1={192} x2={435} y2={192} stroke="#888" strokeWidth="2"/>
        <text x={425} y={205} textAnchor="middle" fontSize="11" fill="#888">pivot</text>

        {/* Left side: force F applied, arm a */}
        <line x1={320} y1={100} x2={320} y2={153} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#emA)"/>
        <text x={328} y={95} fontSize="12" fill="#c0392b" fontWeight="700">k·x</text>
        <line x1={310} y1={165} x2={420} y2={165} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4"/>
        <text x={365} y={182} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">a</text>

        {/* Right side: mass, arm b */}
        <rect x={520} y={130} width={36} height={40} rx={4} fill="#2E75B6" stroke="#1a5276" strokeWidth="2"/>
        <text x={538} y={154} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">m</text>
        <line x1={435} y1={165} x2={556} y2={165} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4"/>
        <text x={495} y={182} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">b</text>

        {/* Displacement x at left, y=b/a·x at right */}
        <line x1={320} y1={153} x2={320} y2={135} stroke="#c0392b" strokeWidth="2" strokeDasharray="3"/>
        <text x={308} y={133} textAnchor="end" fontSize="11" fill="#c0392b">x</text>
        <line x1={538} y1={128} x2={538} y2={100} stroke="#2E75B6" strokeWidth="2" strokeDasharray="3"/>
        <text x={546} y={98} fontSize="11" fill="#2E75B6">(b/a)x</text>

        <rect x={306} y={220} width={258} height={54} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={312} y={238} fontSize="12" fill="#7B0D0D" fontWeight="700">Lever ratio:  r = b/a</text>
        <text x={312} y={256} fontSize="11" fill="#444">Velocity at m: ẋ_m = r·ẋ</text>
        <text x={312} y={272} fontSize="11" fill="#444">KE_m = ½m(rẋ)² = ½(mr²)ẋ²</text>

        <rect x={306} y={280} width={258} height={86} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={312} y={298} fontSize="12" fill="#7B0D0D" fontWeight="700">Reflected mass to ref. DOF x:</text>
        <text x={312} y={316} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">m_eq = m·r² = m·(b/a)²</text>
        <text x={312} y={334} fontSize="11" fill="#444">Large r → large m_eq (amplified)</text>
        <text x={312} y={352} fontSize="11" fill="#444">Small r → small m_eq (reduced)</text>
        <text x={312} y={368} fontSize="11" fill="#555">Same logic as gear reflected inertia</text>

        {/* ── RIGHT: Summary table of corrections ── */}
        <rect x={580} y={10} width={310} height={380} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={735} y={30} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Mass Correction Factors</text>

        {/* Table header */}
        <rect x={586} y={38} width={298} height={26} rx={2} fill="#c0392b"/>
        <text x={650} y={55} fontSize="11" fill="#fff" fontWeight="700">Element</text>
        <text x={800} y={55} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">m_eq addition</text>

        {[
          { el:'Lumped mass m',              cor:'m  (exactly)',                  bg:'#fff5f5' },
          { el:'Spring (mass mₛ)',            cor:'mₛ/3',                          bg:'#fff'    },
          { el:'Cantilever beam (mass m_b)',  cor:'33m_b/140 ≈ 0.236m_b',         bg:'#fff5f5' },
          { el:'Simply supp. beam (m_b)',     cor:'17m_b/35 ≈ 0.486m_b',          bg:'#fff'    },
          { el:'Fixed-fixed beam (m_b)',      cor:'13m_b/35 ≈ 0.371m_b',          bg:'#fff5f5' },
          { el:'Gear J₂ on shaft 2 → 1',     cor:'J₂·n²  (n=ω₂/ω₁)',            bg:'#fff'    },
          { el:'Lever mass m at arm b',       cor:'m·(b/a)²',                      bg:'#fff5f5' },
          { el:'Disk/cylinder (solid)',       cor:'½mr²  [rotational]',            bg:'#fff'    },
        ].map(({el,cor,bg},i)=>{
          const ty=64+i*32;
          return (
            <g key={i}>
              <rect x={586} y={ty} width={168} height={30} fill={bg} stroke="#f0cece" strokeWidth="0.5"/>
              <rect x={754} y={ty} width={130} height={30} fill={bg} stroke="#f0cece" strokeWidth="0.5"/>
              <text x={590} y={ty+19} fontSize="10" fill="#333">{el}</text>
              <text x={758} y={ty+19} fontSize="10" fill="#7B0D0D" fontFamily="'Courier New',monospace" fontWeight="600">{cor}</text>
            </g>
          );
        })}

        {/* Derivation note */}
        <rect x={586} y={324} width={298} height={62} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={592} y={340} fontSize="11" fill="#7B0D0D" fontWeight="700">How corrections are derived:</text>
        <text x={592} y={356} fontSize="10" fill="#444">KE = ½∫ρA(ẋ(x)/ẋ_tip)²dx × ẋ_tip²</text>
        <text x={592} y={370} fontSize="10" fill="#444">= ½ m_eq ẋ_tip²  →  equate to get m_eq</text>
        <text x={592} y={384} fontSize="10" fill="#555" fontStyle="italic">Same principle as Rayleigh's method</text>
      </svg>
      <div style={S.dCaption}>
        Equivalent mass is found by matching kinetic energy: any element moving at velocity v_i relative to the reference DOF contributes m_i·(v_i/v_ref)² to m_eq. Springs contribute mₛ/3 because their velocity varies linearly from zero at the wall to ẋ at the mass. Gears and levers scale inertia by the square of their speed ratio — a key reason gearboxes dramatically change system dynamics.
      </div>
    </div>
  );
}

// ── Diagram 7: Worked Examples — Computing ωₙ ────────────────────
function DiagramWorkedExamples() {
  const W=900, H=360;

  const examples = [
    {
      x: 10, w: 270,
      title: 'Example 1: Series springs + mass',
      color: '#27ae60', bg: '#f0fff4',
      given: ['k₁ = 4000 N/m', 'k₂ = 6000 N/m', 'm = 5 kg'],
      steps: [
        '1/k_eq = 1/4000 + 1/6000',
        '       = 3/12000 + 2/12000',
        'k_eq  = 12000/5 = 2400 N/m',
        'ωₙ = √(2400/5) = √480',
        'ωₙ = 21.9 rad/s',
        'fₙ = 21.9/(2π) = 3.49 Hz',
      ],
    },
    {
      x: 290, w: 270,
      title: 'Example 2: Cantilever + tip mass',
      color: '#2E75B6', bg: '#eaf3ff',
      given: ['E = 200 GPa', 'I = 1×10⁻⁸ m⁴', 'L = 0.5 m', 'm = 2 kg', 'm_b = 0.3 kg'],
      steps: [
        'k_eq = 3EI/L³',
        '     = 3×200e9×1e-8/0.125',
        '     = 48000 N/m',
        'm_eq = 2 + (33/140)×0.3',
        '     = 2 + 0.071 = 2.071 kg',
        'ωₙ = √(48000/2.071) = 152 rad/s',
      ],
    },
    {
      x: 570, w: 320,
      title: 'Example 3: Spring with mass (Rayleigh)',
      color: '#e07b00', bg: '#fff5e6',
      given: ['k = 3000 N/m', 'm = 4 kg (end mass)', 'mₛ = 0.6 kg (spring mass)'],
      steps: [
        'Without spring mass:',
        '  ωₙ = √(3000/4) = 27.39 rad/s',
        'Rayleigh correction:',
        '  m_eq = m + mₛ/3 = 4 + 0.2',
        '       = 4.2 kg',
        '  ωₙ = √(3000/4.2) = 26.73 rad/s',
        'Error without correction: 2.5%',
      ],
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Worked Examples — Finding ωₙ from Equivalent System Parameters</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {examples.map(({x,w,title,color,bg,given,steps})=>(
          <g key={x}>
            <rect x={x} y={10} width={w} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
            <rect x={x} y={10} width={w} height={36} rx={5} fill={color} opacity="0.15"/>
            <text x={x+w/2} y={32} textAnchor="middle" fontSize="12" fill={color} fontWeight="700">{title}</text>

            {/* Given */}
            <rect x={x+8} y={52} width={w-16} height={given.length*18+12} rx={3} fill={bg} stroke={color} strokeWidth="1"/>
            <text x={x+14} y={68} fontSize="11" fill="#7B0D0D" fontWeight="700">Given:</text>
            {given.map((g,i)=>(
              <text key={i} x={x+14} y={84+i*18} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{g}</text>
            ))}

            {/* Steps */}
            <text x={x+14} y={given.length*18+82} fontSize="11" fill="#7B0D0D" fontWeight="700">Solution:</text>
            {steps.map((s,i)=>{
              const isResult = s.startsWith('ωₙ =') || s.startsWith('fₙ =') || s.startsWith('  ωₙ =') || s.includes('rad/s') || s.includes('Hz') || s.includes('Error');
              return (
                <text key={i} x={x+14} y={given.length*18+98+i*19}
                  fontSize={isResult?12:11}
                  fill={isResult?color:'#333'}
                  fontFamily="'Courier New',monospace"
                  fontWeight={isResult?'700':'400'}>
                  {s}
                </text>
              );
            })}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Three worked examples applying the equivalent stiffness/mass reduction and Rayleigh correction. Example 1 uses series springs. Example 2 uses the cantilever beam stiffness formula with distributed beam mass correction. Example 3 compares the result with and without the Rayleigh spring-mass correction, showing a 2.5% error when the spring mass is ignored — significant when mₛ is not negligible compared to m.
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────
export default function FreeVibrations() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Free Vibrations — Initial Conditions, Energy Methods &amp; Equivalent Systems</h1>
      <p style={S.p}>
        Free vibration occurs when a system oscillates under only its own internal restoring forces after being disturbed from equilibrium — with no ongoing external excitation. Understanding free vibration means knowing three things: how the response depends on the initial conditions (displacement and velocity) that launched the motion; how to estimate the natural frequency using energy methods when the full equation of motion is hard to form; and how to simplify complex systems with multiple springs, beams, gears, and distributed masses into a single equivalent oscillator before applying the SDOF formulas.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "General undamped free response: x(t) = x₀cos(ωₙt) + (ẋ₀/ωₙ)sin(ωₙt) = A·cos(ωₙt − φ)",
        "Amplitude A = √(x₀² + (ẋ₀/ωₙ)²) — set by ICs; frequency ωₙ = √(k/m) — set by system only",
        "Phase plane: each IC traces a unique closed ellipse; undamped trajectories never change",
        "Rayleigh's method: assume a shape, set T_max = V_max → upper bound on ωₙ",
        "Spring mass correction: add mₛ/3 to end mass m — velocity varies linearly along spring",
        "Cantilever beam: k_eq = 3EI/L³, add 33m_b/140 to m_eq for beam self-mass",
        "Simply supported beam (mid-span): k_eq = 48EI/L³",
        "Gear/lever reflected inertia: J_eq = J₁ + J₂·n²  where n = speed ratio ω₂/ω₁",
      ]}/>

      <h2 style={S.h2}>Response from Initial Conditions</h2>
      <p style={S.p}>
        The free undamped equation of motion mẍ + kx = 0 is a second-order ODE whose general solution is a sinusoid at ωₙ. Two constants of integration are fixed by the two initial conditions — the displacement x₀ and velocity ẋ₀ at t = 0. Once these are known, the subsequent motion is completely determined for all time. The three cases below illustrate how different ICs produce different amplitudes and phases, but always at the same frequency ωₙ.
      </p>
      <DiagramInitialConditions/>
      <Table2 rows={[
        ["General solution","x(t) = x₀ cos(ωₙt) + (ẋ₀/ωₙ) sin(ωₙt)\nEquivalently: x(t) = A cos(ωₙt − φ)"],
        ["Amplitude A","A = √(x₀² + (ẋ₀/ωₙ)²)\nA = 0 only if x₀=0 AND ẋ₀=0 (trivial equilibrium)\nA depends on ICs, not on k or m directly"],
        ["Phase angle φ","φ = arctan(ẋ₀ / (ωₙ x₀))\nPure displacement IC (ẋ₀=0): φ=0 → pure cosine\nPure velocity IC (x₀=0): φ=π/2 → pure sine"],
        ["Pure displacement start","x(t) = x₀ cos(ωₙt)\nAll energy starts as potential: V₀ = ½kx₀²\nKinetic energy zero at t=0"],
        ["Pure velocity start","x(t) = (ẋ₀/ωₙ) sin(ωₙt)\nAll energy starts as kinetic: T₀ = ½mẋ₀²\nDisplacement zero at t=0"],
        ["Frequency independence","ωₙ = √(k/m) — completely independent of amplitude A\nThis is a fundamental property of linear systems only\nNonlinear systems (e.g. pendulum) have amplitude-dependent frequency"],
      ]}/>

      <h2 style={S.h2}>Phase Plane — Visualising Initial Conditions</h2>
      <p style={S.p}>
        The phase plane plots velocity ẋ against displacement x. Each choice of initial conditions (x₀, ẋ₀) places the system at a specific point in this plane, and the subsequent free motion traces a path from that point. For undamped systems, these paths are closed ellipses — the system returns exactly to its starting point after one period. The size of the ellipse encodes the total energy; its shape is fixed by the ratio k/m (equivalently ωₙ).
      </p>
      <DiagramPhasePlane/>
      <Table2 rows={[
        ["Phase portrait ellipse","(x/A)² + (ẋ/(Aωₙ))² = 1\nSemi-axes: A in x-direction, Aωₙ in ẋ-direction\nAll points on same ellipse have the same energy"],
        ["Direction of travel","Clockwise: in upper half (ẋ>0) x increases; in lower half (ẋ<0) x decreases\nCompletes one loop in time T = 2π/ωₙ"],
        ["Effect of ICs on ellipse","Larger x₀ or ẋ₀ → larger ellipse (more energy)\nChanging ICs changes which ellipse is traversed\nCannot jump between ellipses without damping or forcing"],
        ["Damped case","Spiral inward toward origin (energy lost each cycle)\nSpiral rate controlled by ζ\nCritically damped: no spiral, direct approach to origin"],
        ["Energy on ellipse","At x=±A (turning points, ẋ=0): all potential E=½kA²\nAt x=0 (ẋ=±Aωₙ): all kinetic E=½m(Aωₙ)²\nBoth equal ½kA² confirming energy conservation"],
      ]}/>

      <h2 style={S.h2}>Rayleigh's Energy Method</h2>
      <p style={S.p}>
        Rayleigh's method provides a powerful way to estimate the natural frequency without explicitly forming and solving the full equation of motion. The key insight is that in simple harmonic motion, the maximum kinetic energy (at the equilibrium position, where displacement is zero) must equal the maximum potential energy (at the amplitude, where velocity is zero). By assuming a plausible deflection shape and equating these two extremes, ωₙ² can be read off directly. The method always gives an upper bound on the true ωₙ — any assumed shape that is not the exact mode shape adds artificial stiffness.
      </p>
      <DiagramRayleigh/>
      <DiagramRayleighEnergy/>
      <Table2 rows={[
        ["Rayleigh's principle","ωₙ² = V_max / T_max*   where T_max* = T_max / ω²\nEquivalently: ωₙ² = (∫EI[u″]²dx) / (∫ρA[u]²dx)\nNumerator = stiffness contribution; denominator = mass contribution"],
        ["Upper bound property","Any assumed shape ≠ true mode gives ωₙ_Rayleigh ≥ ωₙ_exact\nBecause wrong shape introduces extra constraint (artificial stiffness)\nImproving the shape always lowers the estimate toward the true value"],
        ["Spring with mass mₛ","Assume linear velocity profile: v(x) = (x/L)ẋ_tip\nT_spring = ∫₀ᴸ ½(mₛ/L)(xẋ_tip/L)²dx = ½(mₛ/3)ẋ_tip²\nm_eff,spring = mₛ/3 → add to end mass m"],
        ["Cantilever shape","Assume cubic: u(x) = (3x²/2L - x³/2L³) (exact static deflected shape)\nGives exact ωₙ when tip mass dominates; 33m_b/140 correction for beam mass"],
        ["Rayleigh–Ritz method","Extend Rayleigh by using a combination of trial functions\nMinimise ωₙ² over combination coefficients\nBasis of finite element method (shape functions)"],
        ["Practical accuracy","Simple polynomial shapes typically give ωₙ within 1–5% of exact\nFor uniform beams the static deflection shape is nearly exact (< 0.2% error)\nGood first estimate before full FEA"],
      ]}/>

      <h2 style={S.h2}>Equivalent Stiffness</h2>
      <p style={S.p}>
        Before applying ωₙ = √(k/m) to any real system, the network of springs, beams, and structural connections must be reduced to a single equivalent stiffness k_eq at the chosen reference degree of freedom. Springs in series add their compliances (flexibilities); springs in parallel add their stiffnesses directly. Structural elements such as beams contribute a point stiffness that depends on the boundary conditions, loading position, and flexural rigidity EI.
      </p>
      <DiagramEquivStiffness/>
      <Table2 rows={[
        ["Springs in series","1/k_eq = 1/k₁ + 1/k₂ + … + 1/kₙ\nThe softest spring dominates — chain is weaker than weakest link\nSame force through each; displacements add: x_total = F/k₁ + F/k₂ + …"],
        ["Springs in parallel","k_eq = k₁ + k₂ + … + kₙ\nSame displacement; forces add — springs share the load\nEquivalent spring stiffer than any single member"],
        ["Cantilever beam (tip load)","k_eq = 3EI/L³\nδ_tip = FL³/(3EI) — from double integration of EI·v″ = M(x)\nE = Young's modulus, I = second moment of area"],
        ["Simply supported (mid-span)","k_eq = 48EI/L³\nδ_mid = FL³/(48EI)\nSixteen times stiffer than cantilever of same EI and L"],
        ["Fixed-fixed beam (mid-span)","k_eq = 192EI/L³\nδ_mid = FL³/(192EI)\nFour times stiffer than simply supported due to clamped ends"],
        ["Torsional shaft","k_torsion = GJ/L   [N·m/rad]  where J = polar moment, G = shear modulus\nFor solid circular: J = πd⁴/32\nShafts in series: 1/k_t_eq = 1/k_t1 + 1/k_t2"],
        ["Combining different types","Identify reference DOF (e.g. tip deflection of beam)\nExpress all stiffness contributions in terms of that DOF\nAdd stiffnesses that act at same DOF; use series rule if in series"],
      ]}/>

      <h2 style={S.h2}>Equivalent Mass</h2>
      <p style={S.p}>
        The equivalent mass m_eq at the reference DOF captures the kinetic energy of all moving parts. The fundamental principle is simple: equate ½m_eq ẋ_ref² to the total kinetic energy of the system expressed in terms of the reference velocity ẋ_ref. Elements moving at a fraction of ẋ_ref contribute proportionally less; elements moving faster (via gears or levers) contribute proportionally more. Rayleigh's method provides the systematic way to handle continuously distributed mass.
      </p>
      <DiagramEquivMass/>
      <Table2 rows={[
        ["Lumped mass at reference","m_eq = m (trivially — moving at ẋ_ref exactly)\nMultiple lumped masses: m_eq = Σ mᵢ(ẋᵢ/ẋ_ref)²"],
        ["Spring self-mass mₛ","m_eq contribution = mₛ/3\nDerived by Rayleigh: linear velocity profile v(x)=ẋ·x/L\nKE_spring = ½(mₛ/3)ẋ²"],
        ["Cantilever beam mass m_b","m_eq contribution = (33/140)m_b ≈ 0.236 m_b\nAssumed shape: u(x) = (3x²L − x³)/(2L³) (static cubic)\nMore accurate than mₛ/3 for beams (shape is better known)"],
        ["Simply supported beam","m_eq contribution = (17/35)m_b ≈ 0.486 m_b\nMid-span load shape: u(x) = sin(πx/L) approximation"],
        ["Gear reflected inertia","J_eq = J₁ + J₂·n²   where n = ω₂/ω₁\nFor n < 1 (speed reduction): J₂ contributes less — good for motors\nFor n > 1 (speed increase): J₂ contributes more — avoid if possible"],
        ["Lever reflected mass","m_eq = m_lever_arm + m_load·(b/a)²\nKE must be the same in original and equivalent system\nb/a = ratio of load arm to reference arm"],
        ["Why n² scaling?","KE = ½Iω² = ½J(nω_ref)² = ½(Jn²)ω_ref²\nThe velocity scales with n, KE with n²\nHalving gear ratio reduces reflected inertia by factor 4"],
      ]}/>

      <h2 style={S.h2}>Worked Examples</h2>
      <p style={S.p}>
        The three examples below walk through the complete reduction process from a physical system to a single ωₙ value, including the Rayleigh spring-mass correction.
      </p>
      <DiagramWorkedExamples/>
      <Table2 rows={[
        ["Procedure summary","1. Identify reference DOF\n2. Compute k_eq (springs in series/parallel; beam formulae)\n3. Compute m_eq (lumped masses + distributed corrections)\n4. Apply ωₙ = √(k_eq / m_eq)"],
        ["Series spring trap","Series springs appear in many systems (two compliance paths back-to-back)\nMost common error: using k₁+k₂ instead of k₁k₂/(k₁+k₂)\nCheck: k_eq must always be less than the smaller spring"],
        ["When to apply mₛ/3","When spring mass mₛ > ~5% of end mass m\nFor mₛ/m = 0.05: correction shifts ωₙ by ~0.8%\nFor mₛ/m = 0.3: correction shifts ωₙ by ~4.7% (significant)"],
        ["Beam stiffness formula checks","Check EI units: [N·m²]\nCheck L³ units: [m³]\nk_eq = 3EI/L³ units: [N·m²/m³] = [N/m] ✓"],
        ["Rayleigh accuracy guideline","Static deflected shape → typically < 1% error for first mode\nHigher polynomial → diminishing returns for 1 DOF\nFor multi-mode: use Rayleigh-Ritz or FEA"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Free response:  x(t) = x₀cos(ωₙt) + (ẋ₀/ωₙ)sin(ωₙt) = A·cos(ωₙt − φ)",
        "Amplitude:  A = √(x₀² + (ẋ₀/ωₙ)²)   |   Phase:  φ = arctan(ẋ₀/(ωₙx₀))",
        "Rayleigh quotient:  ωₙ² = V_max / T_max*  =  ∫EI(u″)²dx / ∫ρA(u)²dx",
        "Spring mass correction:  m_eq = m + mₛ/3",
        "Cantilever:  k_eq = 3EI/L³   |   m_eq = m_tip + (33/140)m_beam",
        "Simply supported (mid-span):  k_eq = 48EI/L³",
        "Fixed-fixed (mid-span):  k_eq = 192EI/L³",
        "Springs in series:  1/k_eq = 1/k₁ + 1/k₂   |   Parallel:  k_eq = k₁ + k₂",
        "Gear reflected inertia:  J_eq = J₁ + J₂·n²   (n = ω₂/ω₁)",
        "Lever reflected mass:  m_eq = m·(b/a)²",
      ]}/>
    </div>
  );
}